/**
 * Tenant havuzu veri tipleri ve ekranda kullanılan Türkçe etiket / renk eşlemeleri.
 *
 * Sözleşme: icrapro-laravel-api  Docs/TENANT-POOL-ADMIN-API.md
 * Taban yol: {icwApiUrl}admin/tenant-pool
 */

export type TenantPoolStatus =
    "available" | "provisioning" | "reserved" | "assigned" | "error" | "orphaned" | "destroying";

export type TenantPoolHealthLevel = "ok" | "warning" | "critical" | "disabled";

export type TenantPoolEventLevel = "info" | "warning" | "error" | "critical";

/** Liste ucundan dönen havuz satırı. Detay ucunda ek alanlar da dolu gelir. */
export interface TenantPoolRow {
    id: number;
    tenant_id: string | null;
    db_name: string | null;
    status: TenantPoolStatus;
    stage: string | null;
    error_code: string | null;
    error_stage: string | null;
    error_message: string | null;
    assign_attempts: number | null;
    reserved_by: string | null;
    assigned_to: string | null;
    reserved_until: string | null;
    assigned_at: string | null;
    provisioned_at: string | null;
    failed_at: string | null;
    heartbeat_at: string | null;
    created_at: string | null;
    deleted_at: string | null;
    /* yalnızca GET /tenants/{id} yanıtında */
    migration_fingerprint?: string | null;
    provisioning_started_at?: string | null;
    reserved_at?: string | null;
    license_id?: number | null;
    updated_at?: string | null;
}

export interface TenantPoolEvent {
    id: number;
    tenant_pool_id: number | null;
    tenant_id: string | null;
    db_name: string | null;
    event: string;
    stage: string | null;
    level: TenantPoolEventLevel;
    error_code: string | null;
    message: string;
    context: any;
    created_at: string;
}

export interface TenantPoolCircuit {
    state: string;
    failure_streak: number;
    threshold: number;
    circuit_fingerprint: string | null;
    current_fingerprint: string | null;
    /** Yalnızca GET /circuit yanıtında gelir; summary.circuit içinde bulunmayabilir. */
    auto_reset_pending?: boolean;
    last_error_code: string | null;
    last_error_message: string | null;
    last_failure_at: string | null;
    opened_at: string | null;
    closed_at: string | null;
}

export interface TenantPoolCounts {
    available: number;
    provisioning: number;
    reserved: number;
    assigned: number;
    error: number;
    orphaned: number;
    destroying: number;
}

export interface TenantPoolCapacity {
    ready: number;
    threshold: number;
    max_pool_size: number;
    needed: number;
    fill_ratio: number;
}

export interface TenantPoolConfig {
    reservation_time: number;
    heartbeat_timeout_minutes: number;
    orphan_grace_minutes: number;
    error_retention_days: number;
    max_per_run: number;
    circuit_failure_threshold: number;
}

export interface TenantPoolSummary {
    enabled: boolean;
    counts: TenantPoolCounts;
    capacity: TenantPoolCapacity;
    circuit: TenantPoolCircuit;
    health: {
        level: TenantPoolHealthLevel;
        reasons: string[];
    };
    config: TenantPoolConfig;
    mutations_enabled: boolean;
}

export interface TenantPoolPagination {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
}

/* ------------------------------------------------------------------ */
/* Görsel sözleşme                                                      */
/* ------------------------------------------------------------------ */

interface Meta {
    text: string;
    color: string;
    icon: string;
    hint: string;
}

/** Durum sırası: ekrandaki sayaç şeridi de bu sırayı kullanır. */
export const TENANT_POOL_STATUSES: TenantPoolStatus[] = [
    "available", "provisioning", "reserved", "assigned", "error", "orphaned", "destroying"
];

const STATUS_META: { [key: string]: Meta } = {
    available: {
        text: "Hazır", color: "success", icon: "mdi-check-circle",
        hint: "Havuzda bekliyor"
    },
    provisioning: {
        text: "Üretiliyor", color: "info", icon: "mdi-progress-wrench",
        hint: "Migration'lar koşuyor"
    },
    reserved: {
        text: "Rezerve", color: "cyan darken-2", icon: "mdi-lock-clock",
        hint: "Bir satışa kilitli"
    },
    assigned: {
        text: "Müşteride", color: "blue-grey", icon: "mdi-account-check",
        hint: "Müşteriye atanmış"
    },
    error: {
        text: "Karantina", color: "error", icon: "mdi-alert-octagon",
        hint: "İncelenmeyi bekliyor; havuza geri dönmez"
    },
    orphaned: {
        text: "Sahipsiz", color: "orange darken-2", icon: "mdi-help-circle",
        hint: "Üretim yarıda kalmış"
    },
    destroying: {
        text: "İmha ediliyor", color: "grey darken-1", icon: "mdi-delete-clock",
        hint: "Veritabanı düşürülüyor"
    }
};

const UNKNOWN_META: Meta = {
    text: "Bilinmiyor", color: "grey", icon: "mdi-help", hint: ""
};

export function statusMeta(status: string | null | undefined): Meta {
    if (!status) {
        return UNKNOWN_META;
    }
    return STATUS_META[status] || {...UNKNOWN_META, text: status};
}

/**
 * `disabled` havuzun kapalı olduğunu söyler; hata değildir, bu yüzden kırmızı değil gri.
 */
const HEALTH_META: { [key: string]: Meta } = {
    ok: {
        text: "Havuz sağlıklı", color: "success", icon: "mdi-check-circle",
        hint: "Tüm göstergeler normal"
    },
    warning: {
        text: "Dikkat gerekiyor", color: "warning", icon: "mdi-alert",
        hint: "Havuz çalışıyor, ancak incelenmesi gereken satırlar var"
    },
    critical: {
        text: "Kritik", color: "error", icon: "mdi-alert-octagon",
        hint: "Havuz hedefini karşılayamıyor"
    },
    disabled: {
        text: "Havuz kapalı", color: "grey darken-1", icon: "mdi-power-plug-off",
        hint: "Satışlar legacy akışla ilerliyor — bu bir hata değildir"
    }
};

export function healthMeta(level: string | null | undefined): Meta {
    if (!level) {
        return UNKNOWN_META;
    }
    return HEALTH_META[level] || {...UNKNOWN_META, text: level};
}

const EVENT_LEVEL_META: { [key: string]: Meta } = {
    info: {text: "Bilgi", color: "info", icon: "mdi-information", hint: ""},
    warning: {text: "Uyarı", color: "warning", icon: "mdi-alert", hint: ""},
    error: {text: "Hata", color: "error", icon: "mdi-close-circle", hint: ""},
    critical: {text: "Kritik", color: "red darken-4", icon: "mdi-alert-octagon", hint: ""}
};

export function eventLevelMeta(level: string | null | undefined): Meta {
    if (!level) {
        return UNKNOWN_META;
    }
    return EVENT_LEVEL_META[level] || {...UNKNOWN_META, text: level};
}

/** `level` filtresi "en az bu seviye" anlamındadır. */
export const EVENT_LEVEL_OPTIONS = [
    {text: "Tümü", value: ""},
    {text: "Bilgi ve üstü", value: "info"},
    {text: "Uyarı ve üstü", value: "warning"},
    {text: "Hata ve üstü", value: "error"},
    {text: "Yalnızca kritik", value: "critical"}
];

/** API'nin kabul ettiği sıralama alanları — başkası gönderilirse 422 döner. */
export const SORTABLE_FIELDS = ["id", "created_at", "failed_at", "assigned_at", "provisioned_at"];

/** Sunucu saat diliminde gelen ISO-8601 damgasını yerelleştirir. */
export function formatDateTime(value: string | null | undefined): string {
    if (!value) {
        return "-";
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleString("tr-TR", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit"
    });
}

/** Zaman çizelgesinde "3 dk önce" gibi göreli bir ipucu göstermek için. */
export function relativeTime(value: string | null | undefined): string {
    if (!value) {
        return "";
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return "";
    }
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 0) {
        return "az sonra";
    }
    if (diff < 60) {
        return diff + " sn önce";
    }
    if (diff < 3600) {
        return Math.floor(diff / 60) + " dk önce";
    }
    if (diff < 86400) {
        return Math.floor(diff / 3600) + " sa önce";
    }
    return Math.floor(diff / 86400) + " gün önce";
}
